import { Stack, Text, Tooltip, Inline, vars, breakpoints } from '@etg/wings';
import { CarrierLogo } from '@eti/components';
import { css, mediaQueries } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import type { Carrier } from '../../../../models';

const carrierLogoCss = css`
  max-width: 24px;

  @media ${mediaQueries.medium.up} {
    max-width: 30px;
  }
`;

const carrierLogoWrapperCss = (isSingle: boolean) => css`
  position: relative;

  @media (min-width: ${breakpoints._1440}) {
    ${isSingle && 'width: 68px'};
    justify-content: flex-end;
  }
`;

const moreCarrierCss = css`
  font-size: 0.75rem;
  font-weight: 400;
`;

const carrierIconsButtonCss = css`
  border: 2px solid transparent;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  margin-block: -2px;
  margin-inline-start: -2px;
  padding: 2px;
  width: fit-content;

  &:focus-visible {
    border: 2px solid ${vars.colors.focusRing};
    transition: all 0.2s ease;
  }
`;

const moreCarriersTextStyles = css`
  background: #fff;
  border: 1px solid #737373;
  bottom: -10px;
  color: #737373;
  font-size: 0.75rem;
  height: 18px;
  padding: 0 2px;
  position: absolute;
  right: 0;
  width: 18px;
`;

interface TripDetailsBoundCarrierLogoProps {
  marketingCarriers: Carrier[];
}

const TripDetailsBoundCarrierLogo = ({ marketingCarriers }: TripDetailsBoundCarrierLogoProps) => {
  const { t } = useTranslation();
  const isXLDevice = window.matchMedia(`(min-width: ${breakpoints._1440})`).matches;
  const isMobileDevice = window.matchMedia(`(min-width: ${breakpoints._768})`).matches;

  const marketingCarrierNames = marketingCarriers
    .map(({ name }) => `<bdi>${name}</bdi>`)
    .join(', ');

  const totalMarketingCarriers = marketingCarriers.length;
  const numberOfMarketingCarriersToShow = totalMarketingCarriers > 1 && isXLDevice ? 2 : 1;
  const showTooltip = totalMarketingCarriers > 2 ? 2 : totalMarketingCarriers;

  const logos = (
    <>
      <Inline className={carrierLogoWrapperCss(marketingCarriers.length === 1)} spacing={8}>
        {marketingCarriers.slice(0, numberOfMarketingCarriersToShow).map(({ code, name, logo }) => (
          <CarrierLogo
            className={carrierLogoCss}
            data-testid="tripDetails-carrier-logo"
            id={code || ''}
            imageAlt={name}
            key={code}
            logo={logo}
          />
        ))}
        {!isXLDevice && totalMarketingCarriers > 1 && (
          <div className={moreCarriersTextStyles} data-testid="tripDetails-carrier-text">
            +{totalMarketingCarriers - 1}
          </div>
        )}
      </Inline>
      {isXLDevice && totalMarketingCarriers > 2 && (
        <p className={moreCarrierCss}>
          {t('TripDetails.MoreCarriers.Text', totalMarketingCarriers - 2)}
        </p>
      )}
    </>
  );

  return (
    <Stack aria-hidden data-testid="tripDetails-bound-carrier-logo" spacing={4}>
      {isMobileDevice ? (
        <Tooltip
          content={<Text asHtml>{marketingCarrierNames}</Text>}
          hidden={!showTooltip}
          title={t('Searchform.Carriers.Label')}
          trigger="click"
        >
          <Stack
            align="start"
            className={carrierIconsButtonCss}
            data-testid="tripDetails-carriers-logo"
            spacing={4}
            tabIndex={0}
          >
            {logos}
          </Stack>
        </Tooltip>
      ) : (
        <Stack
          align="start"
          className={carrierIconsButtonCss}
          data-testid="tripDetails-carriers-logo"
          spacing={4}
        >
          {logos}
        </Stack>
      )}
    </Stack>
  );
};

export { TripDetailsBoundCarrierLogo };
